import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { cn } from "@/lib/cn";

type Variant = "primary" | "secondary" | "ghost" | "danger";
type Size = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  leadingIcon?: ReactNode;
  trailingIcon?: ReactNode;
}

const VARIANT: Record<Variant, string> = {
  primary:
    "bg-brand-600 text-fg-inverse hover:bg-brand-700 active:bg-brand-700 disabled:bg-fg-subtle disabled:text-fg-inverse",
  secondary:
    "bg-canvas text-fg-primary border border-border-default hover:bg-surface-base active:bg-surface-raised disabled:opacity-50",
  ghost:
    "bg-transparent text-fg-primary hover:bg-surface-raised active:bg-surface-sunken disabled:opacity-50",
  danger:
    "bg-danger text-fg-inverse hover:opacity-90 active:opacity-95 disabled:bg-fg-subtle",
};

const SIZE: Record<Size, string> = {
  sm: "h-8 px-3 text-sm",
  md: "h-10 px-4 text-base",
  lg: "h-12 px-5 text-md",
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = "primary",
    size = "md",
    loading = false,
    leadingIcon,
    trailingIcon,
    className,
    children,
    disabled,
    type = "button",
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={cn(
        "inline-flex items-center justify-center gap-2 rounded-md font-medium transition-colors duration-150 select-none",
        "focus-visible:shadow-focus",
        VARIANT[variant],
        SIZE[size],
        loading && "cursor-progress",
        disabled && "cursor-not-allowed",
        className,
      )}
      {...rest}
    >
      {leadingIcon ? <span className="inline-flex shrink-0">{leadingIcon}</span> : null}
      <span>{children}</span>
      {trailingIcon ? <span className="inline-flex shrink-0">{trailingIcon}</span> : null}
    </button>
  );
});
